import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { UserService, User, Role } from '../services/user.service';
import { AuthService } from '../services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { isValidPhoneNumber } from 'libphonenumber-js';

@Component({
  selector: 'app-edit-roles',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatIconModule],
  templateUrl: './edit-roles.component.html',
  styleUrls: ['./edit-roles.component.css']
})
export class EditRolesComponent implements OnInit {
  users: User[] = [];
  allRoles: Role[] = [];
  isLoading = true;
  error: string | null = null;
  searchTerm = '';
  
  selectedUser: User | null = null;
  selectedRoles: Role[] = [];
  showDialog = false;

  userToRemove: User | null = null;
  showRemoveDialog = false;
  showEditPersonalInfoDialog = false;

  currentUserId = localStorage.getItem('id');

  userToResetPassword: User | null = null;
  showResetPasswordDialog = false;
  resetPasswordSuccess = false;
  newPassword = '';
  confirmPassword = '';
  showNewPassword = false;
  showConfirmPassword = false;
  resetPasswordError: string | null = null;

  sortColumn: 'user' | 'roles' = 'user';
  sortDirection: 'asc' | 'desc' = 'asc';

  isOutsourceAdmin = false;

  get visibleUsers(): User[] {
    let result = [...this.users];

    // OutSourceAdmin can only see users with outsource roles
    if (this.isOutsourceAdmin) {
      result = result.filter(user =>
        !this.isCurrentUser(user)
        && user.roles.some(role => role.toLowerCase().startsWith('outsource'))
      );
    }

    return result;
  }

  get filteredUsers(): User[] {
    let result = this.visibleUsers;
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(u =>
        u.username.toLowerCase().includes(term) ||
        u.roles.some(r => r.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.firstname && u.firstname.toLowerCase().includes(term)) ||
        (u.lastname && u.lastname.toLowerCase().includes(term))
      );
    }
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return result.sort((a, b) => {
      if (this.sortColumn === 'user') {
        return dir * a.username.toLowerCase().localeCompare(b.username.toLowerCase());
      } else {
        const rolesA = a.roles.join(', ').toLowerCase();
        const rolesB = b.roles.join(', ').toLowerCase();
        return dir * rolesA.localeCompare(rolesB);
      }
    });
  }

  toggleSort(column: 'user' | 'roles'): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  isCurrentUser(user: User): boolean {
    return user.id.toString() === this.currentUserId;
  }

  constructor(private userService: UserService, private authService: AuthService) {}

  ngOnInit(): void {
    this.isOutsourceAdmin = !this.authService.hasRole('Admin');
    this.loadUsers();
    this.loadRoles();
  }

  loadUsers(): void {
    this.userService.getAllUsers(this.authService.getCompanyId()).subscribe({
      next: (users) => {
        this.users = users.map(u => ({
          ...u,
          profilePhoto: u.profilePhoto || 'images/profile-placeholder.png'
        }));
        // Fetch full user details to get correct profile photo URLs
        this.users.forEach((user, index) => {
          this.userService.getUserById(user.id.toString()).subscribe({
            next: (fullUser) => {
              this.users[index].profilePhoto = fullUser.profilePhoto || 'images/profile-placeholder.png';
            }
          });
        });
        this.isLoading = false;

        if (users.length === 1) {
          this.error = 'No users found';
        }
      },
      error: (err) => {
        this.error = 'Failed to load users';
        this.isLoading = false;
        console.error(err);
      }
    });
  }

  loadRoles(): void {
    this.userService.getAllRoles().subscribe({
      next: (roles) => {
        if (this.isOutsourceAdmin) {
          this.allRoles = roles.filter(r => r?.name?.toLowerCase().startsWith('outsource'));
        } else {
          this.allRoles = roles;
        }
      },
      error: (err) => {
        console.error('Failed to load roles', err);
        this.error = 'Failed to load roles';
      }
    });
  }

  openEditDialog(user: User): void {
    this.selectedUser = user;
    this.selectedRoles = user.roles
      .map(roleName => this.allRoles.find(r => r.name === roleName))
      .filter((r): r is Role => r !== undefined);
    this.showDialog = true;
  }

  openEditPersonalInfoDialog(user: User): void {
    this.userService.getUserById(user.id.toString()).subscribe({
      next: (fetchedUser) => {
        this.selectedUser = fetchedUser;
        fetchedUser.firstname = fetchedUser.firstname || '';
        fetchedUser.lastname = fetchedUser.lastname || '';
        fetchedUser.email = fetchedUser.email || '';
        fetchedUser.phoneNumber = fetchedUser.phoneNumber || '';
        fetchedUser.job_title = fetchedUser.job_title || '';
        this.showEditPersonalInfoDialog = true;
      }
    });
  }

  savePersonalInfo(): void {
  if (!this.selectedUser) return;
  
  this.userService.updateUserInfo(this.selectedUser, this.selectedUser.id).subscribe({
    next: () => {
      console.log('User information updated');
      this.closeEditPersonalInfoDialog();
      
      // Reload users to reflect changes
      setTimeout(() => this.loadUsers(), 250);
    },
    error: (err) => {
      console.error('Failed to update user information', err);
      this.error = 'Failed to update user information';
      this.closeEditPersonalInfoDialog();
    }
  });
}

  closeEditPersonalInfoDialog(): void {
    this.showEditPersonalInfoDialog = false;
    this.selectedUser = null;
  }

  closeDialog(): void {
    this.showDialog = false;
  }

  isRoleSelected(role: Role): boolean {
    return this.selectedRoles.some(r => r.id === role.id);
  }

  toggleRole(role: Role): void {
    const index = this.selectedRoles.findIndex(r => r.id === role.id);
    if (index === -1) {
      this.selectedRoles.push(role);
    } else {
      this.selectedRoles.splice(index, 1);
    }
  }

  saveRoles(): void {
    if (!this.selectedUser) return;
    
    const currentRoleIds = this.selectedUser.roles.map(name =>
      this.allRoles.find(role => role.name === name)?.id
    ).filter((id): id is number => id !== undefined);

    const newRoleIds = this.selectedRoles.map(r => r.id);

    const rolesToAdd = newRoleIds.filter(id => !currentRoleIds.includes(id));
    const rolesToRemove = currentRoleIds.filter(id => !newRoleIds.includes(id));

    const userId = this.selectedUser.id;

    rolesToAdd.forEach(roleId => {
      this.userService.assignRoleToUser({ userId, roleId: roleId.toString() }).subscribe({
        next: () => console.log('Role assigned:', roleId),
      });
    });

    rolesToRemove.forEach(roleId => {
      this.userService.removeRoleFromUser({ userId, roleId: roleId.toString() }).subscribe({
        next: () => console.log('Role removed:', roleId),
      });
    });

    this.closeDialog();

    // Reload users after a short delay to reflect changes
    setTimeout(() => this.loadUsers(), 250);
  }
  
  openRemoveDialog(user: User): void {
    this.userToRemove = user;
    this.showRemoveDialog = true;
  }

  closeRemoveDialog(): void {
    this.showRemoveDialog = false;
    this.userToRemove = null;
  }

  confirmRemoveUser(): void {
    if (!this.userToRemove || !this.userToRemove.id) return;
    
    this.userService.deactivateUser(this.userToRemove.id).subscribe({
      next: () => {
        this.users = this.users.filter(u => u.id !== this.userToRemove?.id);
        console.log('User removed:', this.userToRemove?.id);
        this.closeRemoveDialog();
      },
      error: (err) => {
        console.error('Failed to remove user', err);
        this.error = 'Failed to remove user';
        this.closeRemoveDialog();
      }
    });
  }

  openResetPasswordDialog(user: User): void {
    this.userToResetPassword = user;
    this.newPassword = '';
    this.confirmPassword = '';
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.resetPasswordError = null;
    this.showResetPasswordDialog = true;
  }

  closeResetPasswordDialog(): void {
    this.showResetPasswordDialog = false;
    this.userToResetPassword = null;
    this.newPassword = '';
    this.confirmPassword = '';
    this.resetPasswordError = null;
  }

  confirmResetPassword(): void {
    if (!this.userToResetPassword || !this.userToResetPassword.id) return;

    if (!this.newPassword) {
      this.resetPasswordError = 'Please enter a new password.';
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.resetPasswordError = 'Passwords do not match.';
      return;
    }

    this.resetPasswordError = null;

    this.userService.resetUserPassword(this.userToResetPassword.id, this.newPassword).subscribe({
      next: () => {
        if (this.userToResetPassword) {
          console.log('Password reset for user:', this.userToResetPassword.id);
        }
        this.closeResetPasswordDialog();
        this.resetPasswordSuccess = true;
        
        setTimeout(() => {
          const successMessage = document.querySelector('.success-message');
          if (successMessage) {
            successMessage.classList.add('fade-out');

            setTimeout(() => {
              this.resetPasswordSuccess = false;
            }, 500);
          }
        }, 2500);
      },
      error: (err) => {
        console.error('Failed to reset password', err);
        this.error = 'Failed to reset password';
        this.closeResetPasswordDialog();
      }
    });
  }

    validatePhoneFormat(phone: string | undefined): boolean {
    if (!phone) return true;
    return isValidPhoneNumber(phone, 'CA'); 
  }
}
